import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import cn from 'classnames'
import { logError, LogErrorMetrics } from '@lib/tracking/felog'
import { TypeComponentForm } from '@lib/types/component-form'
import { ContentProps, ContentRenderer, FormContentRenderer } from '../renderer'
import { Form } from '@ui-lib/form'
import { Typography } from '@grammarly/badger-ui'
import { ImageAsset } from '../asset/image'
import { useBackgroundStyle } from '../page-section/theme'
import { FormSubmitButton } from '../item/cta-button/form-submit'
import { getButtonColor } from '../item/cta-button/button-color'
import { renderRichTextSingleLineContent } from '../renderer/rich-text'
import { transformToString } from '../../lib/utils/error'
import { gnarSpec } from '../../lib/tracking/gnar'
import { PageContext } from '../../contexts/page-context'
import styles from './form.module.scss'
import heroCardStyles from '../ui-lib/hero/card.module.scss'
import { SectionThemeContext } from '../../contexts/section-theme-context'
import { RequiredOrOptional } from '@ui-lib/form/form-context'
import { SubmitHandler, useForm, FormProvider } from 'react-hook-form'
import { useState } from 'react'
import { rhfToFormData } from './rhf-helpers'
import { useApiService, useCommonServices, useGnarContainerId } from '@lib/hooks'
import { createWebinar } from './utils/create-webinar'
import { useRecaptcha } from 'src/contexts/recaptcha-context'
import { getConfig } from 'src/config'
import { withConditionalRecaptcha } from '@lib/mixins/with-conditional-recaptcha'
import { SALESFORCE_RECAPTCHA_ACTION } from '@lib/types'
import { sendDefault } from './utils/send-default'
import { sendWithRecaptcha } from './utils/send-with-recaptcha'
import { setPardotAssociatedCookies } from './utils/set-pardot-cookies'
import { sendConnectWithSales } from './utils/send-connect-with-sales'
import { Checkbox, Loader } from '@grammarly/design-system'
import { extractUtmParams } from '@lib/utils/url'
import { getUtmParamsFromCookie } from '@components/form/utils/utm-params'
import { usePardotCookieAssociation } from '@components/pardot-association/usePardotCookieAssociation'
import { FormOverrideContext, FormOverrideFieldType } from '@contexts/form-override-context'
import { prefillWithUserLeadInfo } from './utils/user-lead-info'
import { isValidEmail } from '@components/inputs/validations'

const { T3 } = Typography

/**
 * For user information security, we only permit sending user information to
 * approved domains.
 */
const approvedDomains = ['grammarly.com', 'go.grammarly.com']

const submitUrlIsApproved = (url: string): boolean => {
  const parsedUrl = new URL(url)
  return approvedDomains.includes(parsedUrl.hostname)
}

const appendUtmParams = (formData: FormData, query: URLSearchParams) => {
  const utmParamsFromQuery = extractUtmParams(query)
  const utmParams = Object.keys(utmParamsFromQuery).length ? utmParamsFromQuery : getUtmParamsFromCookie()
  if (utmParams) {
    Object.entries(utmParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => formData.append(key, v))
      } else {
        formData.append(key, value)
      }
    })
  }
}

/**
 * If we have conditional field, on some forms it may result in 'undefined' value.
 * 'undefined' triggers different workflows on marketing side and complicates the work.
 */
const normalizeFormValuesToSalesforce = (formValues: Record<string, string>) => {
  const values = { ...formValues }

  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined) {
      values[key] = ''
    }
  })

  return values
}

// `email` is used as primary identifier in Pardot.
export const MultiStepFormEmailFieldName = 'email'

export const FormComponent: React.FC<ContentProps<TypeComponentForm>> = withConditionalRecaptcha(
  getConfig().recaptcha.salesforce,
  ({ structure }) => {
    const {
      ariaLabel,
      background,
      downloadUrl,
      headerImage,
      headerBackground,
      heading,
      includeUtm,
      includeGnarContainerId,
      internalName,
      content,
      submitCtaColor,
      submitCtaText,
      submitUrl,
      indicateRequiredFields,
      openInSameTab,
      webinarId,
      recaptchaEnabled,
      thanksFormOverlayContent,
      associatePardotCookies,
      connectWithSalesEnabled,
      multiStepFormEnabled,
      prefillEnabled,
    } = structure.fields

    const executeRecaptcha = useRecaptcha()
    const [connectWithSales, setConnectWithSales] = useState(false)

    const methods = useForm({ mode: 'onChange' })
    const { handleSubmit, formState, setValue, watch } = methods

    const downloadRef = useRef<HTMLAnchorElement>(null)
    const { pageId, slug } = useContext(PageContext)
    const theme = useContext(SectionThemeContext)
    const backgroundStyles = useBackgroundStyle(theme, background?.color)
    const headerBackgroundStyles = useBackgroundStyle(theme, headerBackground?.color)

    const requiredOrOptionalIndicator = useMemo(() => {
      return indicateRequiredFields ? RequiredOrOptional.Required : RequiredOrOptional.Optional
    }, [indicateRequiredFields])

    const { containerId } = useGnarContainerId()
    const { email: pardotEmail } = usePardotCookieAssociation()
    const { analytics } = useApiService()
    const { geoLocationService } = useCommonServices()

    const [overrideFields, setOverrideFields] = useState<FormOverrideFieldType[]>([])
    const [isPrefilling, setIsPrefilling] = useState(false)

    const emailValue = watch('email', '')

    useEffect(() => {
      if (!multiStepFormEnabled) {
        return
      }
      const emailField = content.find((field) => field.fields.name === MultiStepFormEmailFieldName)
      if (!emailField) {
        throw new Error('Missing email input field in form with multi-step enabled')
      }

      const showEmailFieldForMultiStepForm = multiStepFormEnabled && !pardotEmail
      setOverrideFields([
        {
          name: emailField.fields.name,
          // we want to hide the email field for multi-step forms if pardot email is set
          isHidden: emailField.fields.isHidden && !showEmailFieldForMultiStepForm,
        },
      ])

      // set value to pardot email to fix validation errors from react-hook-form.
      // this must be done manually even after supplying `hiddenInputValue` due to how react-hook-form maintains its own state of values
      // and how it handles validation.
      if (pardotEmail) {
        setValue(MultiStepFormEmailFieldName, pardotEmail)
      }
    }, [content, multiStepFormEnabled, pardotEmail, setValue])

    const prefill = useCallback(
      async (email: string) => {
        setIsPrefilling(true)

        const prefilledFieldNames = await prefillWithUserLeadInfo(
          { analytics, geoLocationService },
          email,
          content,
          setValue
        )
        if (prefilledFieldNames.length > 0) {
          gnarSpec.formPrefillSuccess(internalName, pageId, slug, prefilledFieldNames, submitUrl)
        }

        setIsPrefilling(false)
      },
      [analytics, content, geoLocationService, internalName, pageId, setValue, slug, submitUrl]
    )

    useEffect(() => {
      if (!prefillEnabled || !emailValue || !isValidEmail(emailValue)) {
        return
      }
      const prefillTimeout = setTimeout(() => {
        prefill(emailValue)
      }, 500)
      return () => clearTimeout(prefillTimeout)
    }, [emailValue, prefill, prefillEnabled])

    const onSubmit: SubmitHandler<Record<string, string>> = async (formValues) => {
      try {
        gnarSpec.formSubmit(internalName, pageId, slug, submitUrl)

        if (!submitUrlIsApproved(submitUrl)) {
          throw new Error(
            `URL ${submitUrl} is not approved. Forms may only submit data to approved domains. Please use an approved domain or add your domain to the approved list.`
          )
        }

        const formData = rhfToFormData(normalizeFormValuesToSalesforce(formValues))

        if (multiStepFormEnabled && !formData.has(MultiStepFormEmailFieldName) && pardotEmail) {
          formData.append(MultiStepFormEmailFieldName, pardotEmail)
        }

        if (includeUtm) {
          const query = new URLSearchParams(window.location.search)
          appendUtmParams(formData, query)
        }

        if (includeGnarContainerId) {
          formData.append('gnarcontainerid', containerId)
        }

        let connectWithSalesFormValues
        if (connectWithSales) {
          connectWithSalesFormValues = rhfToFormData({
            email: formValues.email,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            'Assigned Reason Detail': downloadUrl ?? 'Unknown',
          })
        }

        let formSubmitted = false
        if (recaptchaEnabled) {
          try {
            const recaptchaToken = await executeRecaptcha(SALESFORCE_RECAPTCHA_ACTION)

            const response = await sendWithRecaptcha(submitUrl, recaptchaToken, formData)
            if (!response.ok) {
              throw new Error('Recaptcha failed')
            }

            gnarSpec.formRecaptchaSuccess(internalName, pageId, slug, submitUrl)
            formSubmitted = true

            if (connectWithSalesFormValues) {
              await sendConnectWithSales(connectWithSalesFormValues)
            }
          } catch {
            if (!formSubmitted) {
              gnarSpec.formRecaptchaFail(internalName, pageId, slug, submitUrl)
            }
            methods.setError('rootForm', { message: 'Unknown error' })
          }
        }

        if (!formSubmitted) {
          const response = await sendDefault(submitUrl, formData)
          const success = typeof response === 'boolean' ? response : response.ok
          if (success && connectWithSalesFormValues) {
            await sendConnectWithSales(connectWithSalesFormValues)
          }
          formSubmitted = success
        }

        if (formSubmitted && associatePardotCookies) {
          setPardotAssociatedCookies(formValues.email, submitUrl)
        }

        if (webinarId) {
          try {
            const webinarResponse = await createWebinar(webinarId, formValues)
            if (!webinarResponse.ok) {
              throw new Error('Webinar creation failed')
            }
            gnarSpec.formWebinarSuccess(internalName, pageId, slug, submitUrl, webinarId)
          } catch (e) {
            const error = transformToString(e as object)
            gnarSpec.formWebinarFail(internalName, pageId, slug, submitUrl, webinarId, error)
          }
        }

        if (!formSubmitted) {
          throw new Error('Form submission failed')
        }
        gnarSpec.formSuccess(internalName, pageId, slug, submitUrl)
      } catch (e) {
        const error = transformToString(e as object)
        logError('Error submitting form.', LogErrorMetrics.FormSubmitError, {
          error,
        })
        gnarSpec.formFail(internalName, pageId, slug, submitUrl, error)
      } finally {
        downloadRef.current?.click()
      }
    }

    const isThanksOverlayVisible = thanksFormOverlayContent && !downloadUrl && formState.isSubmitted

    return (
      <div className={cn(heroCardStyles.heroCard, ...backgroundStyles, styles.form)} data-component-id={internalName}>
        <div
          className={cn(styles.overlay, isThanksOverlayVisible ? styles.overlayVisible : styles.overlayHidden)}
          role="region"
          aria-label="Thanks content overlay"
          aria-hidden={!isThanksOverlayVisible}
        >
          <ContentRenderer structure={thanksFormOverlayContent} />
        </div>
        {headerImage && (
          <div className={cn(heroCardStyles.imageContainer, ...headerBackgroundStyles)}>
            <ImageAsset className={heroCardStyles.image} structure={headerImage} />
          </div>
        )}
        <FormProvider {...methods}>
          <Form
            ariaLabel={ariaLabel || heading}
            className={heroCardStyles.content}
            id={internalName}
            onSubmit={handleSubmit(onSubmit)}
            requiredOrOptionalIndicator={requiredOrOptionalIndicator}
          >
            <T3 as="h2" className={styles.titleStyle}>
              {heading}
            </T3>
            <FormOverrideContext.Provider value={{ overrideFields }}>
              <FormContentRenderer structure={content} />
            </FormOverrideContext.Provider>
            {connectWithSalesEnabled && (
              <Checkbox onChange={setConnectWithSales}>Connect me with a member of the Grammarly sales team</Checkbox>
            )}
            <div className={styles.actionRow}>
              <FormSubmitButton
                color={getButtonColor(submitCtaColor)}
                download={downloadUrl ? { href: downloadUrl, ref: downloadRef } : undefined}
                text={renderRichTextSingleLineContent(submitCtaText)}
                openInSameTab={openInSameTab}
              />
            </div>
          </Form>
        </FormProvider>

        {isPrefilling && (
          <div className={styles.loaderOverlay}>
            <Loader accessibilityLabel="Loading form">
              <Loader.Circular size="large" />
            </Loader>
          </div>
        )}
      </div>
    )
  }
)
